import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  GcLoadingSpinnerComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import {
  Feature,
  FeatureCollection,
  Geometry,
  LineString,
  Point,
} from 'geojson';
import { map, Observable, of } from 'rxjs';
import { MapApiService } from '../../../span-progress-app/service/api/map-api.service';
import {
  DistributionPointProperties,
  SpanProperties,
} from '../../../span-progress-app/span-progress-entities';
import { ErrorInformationComponent } from '../../error-information/error-information.component';
import { GeoJsonStorageService } from '../../geo-json-storage-service/geo-json-storage.service';
import { SharedModule } from '../../shared.module';
import { isTopLevelDomainFI } from '../../util/deploy-helper';
import { RolloutProgressReportEntities } from '../service/entities';
import { InspectionReportInitService } from '../service/initial-values/inspection-report-init.service';
import { ReportViewComponent } from './v1/report-view.component';
import { ReportViewV2Component } from './v2/report-view-v2/report-view-v2.component';

@Component({
  selector: 'app-report-view-top',
  templateUrl: './report-view-top.component.html',
  imports: [
    CommonModule,
    SharedModule,
    ReportViewComponent,
    IpoTranslatePipe,
    ErrorInformationComponent,
    ReportViewV2Component,
    GcLoadingSpinnerComponent,
  ],
})
export class ReportViewTopComponent implements OnInit {
  private readonly mapService = inject(MapApiService);
  private readonly initService = inject(InspectionReportInitService);
  private readonly geoJsonStorageService = inject(GeoJsonStorageService);

  ifs = input.required<string>();
  typeId = input.required<RolloutProgressReportEntities.v3.MarkerTypes>();
  id = input.required<string>();

  isDeliveryPortalFI: boolean = isTopLevelDomainFI();
  isSiteAndHubReport = this.initService.isSiteAndHubReport;
  isLoading = this.initService.isReportLoading;

  distributionPointGeoJson$: Observable<
    FeatureCollection<GeoJSON.Point, DistributionPointProperties>
  >;
  spanGeoJson$: Observable<FeatureCollection<LineString, SpanProperties>>;
  distributionPointProperties:
    | Feature<GeoJSON.Point, DistributionPointProperties>
    | undefined;
  spanPropertiesArray: Feature<LineString, SpanProperties>[];
  spanProperties: Feature<LineString, SpanProperties> | undefined;

  constructor(
    private readonly title: Title,
    private readonly translate: IpoTranslatePipe,
  ) {
    this.title.setTitle(
      this.translate.transform(this.isDeliveryPortalFI ? 'tabFI' : 'tabSE'),
    );
  }

  ngOnInit(): void {
    if (this.typeId() === 'SPAN') {
      this.getSpanProperties$();
    } else {
      this.getDistributionPoint$();
    }
  }

  getDistributionPoint$ = (): void => {
    this.distributionPointGeoJson$ = this.getGeoJsonData$(
      this.geoJsonStorageService.checkForStoredPoints(this.ifs()),
      () => this.mapService.getDistributionPointGeoJson(this.ifs()),
      this.processDataPoints,
      this.geoJsonStorageService.setPoints,
    );
  };

  getSpanProperties$ = () => {
    this.spanGeoJson$ = this.getGeoJsonData$(
      this.geoJsonStorageService.checkForStoredSpans(this.ifs()),
      () => this.mapService.getSpanGeoJson(this.ifs()),
      this.processDataSpans,
      this.geoJsonStorageService.setSpans,
    );
  };

  getGeoJsonData$ = <T extends Geometry | null>(
    storedData: FeatureCollection<T, any> | null,
    fetchData: () => Observable<FeatureCollection<T, any>>,
    processData: (res: FeatureCollection<T, any>) => void,
    storeData: (
      ifs: string,
      featureCollection: FeatureCollection<T, any>,
    ) => void,
  ): Observable<FeatureCollection<T, any>> => {
    if (storedData) {
      processData(storedData);
      return of(storedData);
    } else {
      return fetchData().pipe(
        map((res) => {
          processData(res);
          storeData(this.ifs(), res);
          return res;
        }),
      );
    }
  };

  processDataPoints = (
    res: FeatureCollection<Point, DistributionPointProperties>,
  ) => {
    this.distributionPointProperties = res.features.find((point) =>
      this.typeId() === 'HP'
        ? (point.properties.leadId ?? point.properties.sourceKey) === this.id()
        : point.properties.id === this.id(),
    );
    if (!this.distributionPointProperties) {
      this.initService.setIsReportLoading(false);
    }
  };

  processDataSpans = (res: FeatureCollection<LineString, SpanProperties>) => {
    this.spanPropertiesArray = res.features;
    this.spanProperties = res.features.find(
      (span) => span.properties.spanId === this.id(),
    );
    if (!this.spanProperties) {
      this.initService.setIsReportLoading(false);
    }
  };
}
